import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { PlatformService } from './platform.service';
import { AsyncSubject, combineLatest } from 'rxjs';
import { ExternalSettingsService } from 'src/app/common/services/external-settings/external-settings.service';
import { AppVersion, ExternalSettings } from 'src/app/common/services/external-settings/external-settings.interface';
import { TranslateService } from '@ngx-translate/core';
import { MEErrorService } from 'src/app/common/error/me-error.service';
import { InAppBrowserService } from './in-app-browser.service';
import { MeConfirmDialogService } from 'src/app/common/components/me-dialog/me-confirm/me-confirm.service';
import { ConfirmDialogConfig, DialogType } from 'src/app/common/components/me-dialog/me-confirm/me-confirm.component';
import { compare } from 'compare-versions';
import { AppService } from './app.service';

@Injectable({
    providedIn: 'root'
})
export class VersionService {

    private externalSettings!: ExternalSettings;
    private static readonly DEFAULT_APP_VERSION_ANNOUNCEMENT: AppVersion = {
        languageCode: "en",
        title: "Update Needed",
        message: "This app version has been retired, please install the newest app version from the store."
    };

    constructor(
        private translate: TranslateService,
        private srvES: ExternalSettingsService,
        private srvError: MEErrorService,
        private srvConfirmDialog: MeConfirmDialogService,
        private srvInAppBrowser: InAppBrowserService
    ) {
    }

    public init() {
        let ret = new AsyncSubject<boolean>();

        if (PlatformService.isBrowser()) {
            ret.next(true);
            ret.complete();
        }
        else {
            combineLatest([this.srvES.externalSettings$]).subscribe(([externalSettings]) => {
                if (!externalSettings) return;
                this.externalSettings = externalSettings;
                let isOkay = this.checkAppVersion();
                ret.next(isOkay);
                ret.complete();
            });
        }
        return ret;
    }

    private checkAppVersion(): boolean {

        if (!this.externalSettings.appVersionEnable) return true;

        let currentVersion = AppService.getAppInfo().version;
        let minVersion = (PlatformService.isIOS()) ? this.externalSettings.minIOSVersion : this.externalSettings.minAndroidVersion;
        minVersion = minVersion.replace(/\s*\([^)]*\)\s*/, "").trim();         // Remove build number (xxx) before comparison

        if (compare(currentVersion, minVersion, "<")) {

            let isForceUpdate = this.externalSettings.forceUserUpdate;
            let appVersionMessage = this.getAppVersionDialogText(this.externalSettings);

            if (isForceUpdate) {
                this.srvError.showDialog(appVersionMessage.title, appVersionMessage.message, 'UPDATE_NOW').subscribe(() => {
                    this.proceedAppUpdate(isForceUpdate, appVersionMessage);
                });
                return false;
            } else {
                let confirmDialogConfig: ConfirmDialogConfig = {
                    dialogType: DialogType.CONFIRM,
                    title: appVersionMessage.title,
                    message: appVersionMessage.message,
                    confirmButtonText: this.translate.instant('UPDATE_NOW'),
                    cancelButtonText: this.translate.instant('UPDATE_LATER')
                }
                this.srvConfirmDialog.showDialog(confirmDialogConfig).subscribe((isConfirm) => {
                    if (isConfirm) {
                        this.proceedAppUpdate(isForceUpdate, appVersionMessage);
                    }
                });
                return true;
            }
        }
        return true;
    }

    private proceedAppUpdate(isForceUpdate: boolean, appVersionMessage: AppVersion) {
        let storeURL = (PlatformService.isIOS()) ? this.externalSettings.appStoreURL : this.externalSettings.playStoreURL;
        this.srvInAppBrowser.openExternalBrowser(storeURL);

        // If it's a force update, close the app for android, show system unavailable for ios (since we can't close the app)
        if (isForceUpdate) {
            if (PlatformService.isAndroid()) {
                App.exitApp();
            }
            else {
                this.srvError.systemAvailable.next(false);
                this.srvError.systemUnAvailableError.next({ title: appVersionMessage.title, message: appVersionMessage.message });
            }
        }
    }

    private getAppVersionDialogText(externalSettings: ExternalSettings): AppVersion {
        // Need to handle credit union to "denormalize" it back to locale
        let language = this.translate.currentLang.replace("-cu", "");
        return externalSettings.appVersion.find(item => item.languageCode === language) || VersionService.DEFAULT_APP_VERSION_ANNOUNCEMENT;
    }
}